import { useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    getMystudioErrorMessage,
    useAddInquiryMessageV4,
    useCreateInquiryV4,
    useMarkInquiryReadV4,
    useMystudioInquiryV4,
    useMystudioInquiriesV4,
} from "@/Common/api/v4";
import { isPreviewDataAllowed } from "@/Common/api/v4/previewGuard";
import MobileInquiryPage, { MobileInquiryPost } from "./MobileInquiryPage";

type MobileInquirySubmitPayload = {
    category: string;
    title: string;
    content: string;
    files: File[];
};

function formatApiDateTime(value?: string) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    const datePart = [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, "0"),
        String(date.getDate()).padStart(2, "0"),
    ].join("-");
    const timePart = [
        String(date.getHours()).padStart(2, "0"),
        String(date.getMinutes()).padStart(2, "0"),
        String(date.getSeconds()).padStart(2, "0"),
    ].join(":");
    return `${datePart} ${timePart}`;
}

/**
 * 고객 문의 화면은 NestJS v4 문의 API만 사용한다.
 * Django 게시판 API로 직접 조회하거나 저장하지 않는다.
 */
export default function QnaList() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { postPk } = useParams<{ postPk?: string }>();
    const inquiryQuery = useMystudioInquiriesV4();
    const inquiryDetailQuery = useMystudioInquiryV4(postPk);
    const createInquiryMutation = useCreateInquiryV4();
    const addMessageMutation = useAddInquiryMessageV4();
    const markReadMutation = useMarkInquiryReadV4();

    const getQnaRoute = (path: string, screen?: string) => {
        const nextParams = new URLSearchParams();
        if (screen) nextParams.set("screen", screen);
        const query = nextParams.toString();
        return query ? `${path}?${query}` : path;
    };

    const posts = useMemo<MobileInquiryPost[]>(() => (
        (inquiryQuery.data?.data?.items || []).map((item, index) => ({
            post_pk: item.id || `inquiry-${index}`,
            post_title: item.title,
            post_contents: item.content,
            category: item.category,
            post_insert_dtm: formatApiDateTime(item.createdAt),
            answer_contents: item.answer || null,
            answer_insert_dtm: formatApiDateTime(item.answeredAt),
            answer_status: item.status === "answered" || item.status === "closed"
                ? "답변완료"
                : "답변대기",
            unread_reply_count: item.unreadReplyCount || 0,
        }))
    ), [inquiryQuery.data]);

    const pathSegments = window.location.pathname.split("/").filter(Boolean);
    const isWriteOpen = pathSegments[pathSegments.length - 1] === "write";
    const selectedPost = useMemo(() => {
        if (!postPk) return null;
        const listPost = posts.find((post) => String(post.post_pk) === postPk) || null;
        const detail = inquiryDetailQuery.data?.data;
        if (!detail) return listPost;
        return {
            post_pk: detail.id,
            post_title: detail.title,
            post_contents: detail.content,
            category: detail.category,
            post_insert_dtm: formatApiDateTime(detail.createdAt),
            answer_contents: detail.answer || null,
            answer_insert_dtm: formatApiDateTime(detail.answeredAt),
            answer_status: detail.status === "answered" || detail.status === "closed"
                ? "답변완료"
                : "답변대기",
            unread_reply_count: detail.unreadReplyCount || 0,
            messages: (detail.messages || []).map((message) => ({
                id: message.id,
                sender_type: message.senderType,
                body: message.body,
                created_at: formatApiDateTime(message.createdAt),
            })),
        };
    }, [inquiryDetailQuery.data, postPk, posts]);

    useEffect(() => {
        if (
            postPk
            && inquiryDetailQuery.isSuccess
            && (inquiryDetailQuery.data?.data?.unreadReplyCount || 0) > 0
            && !markReadMutation.isPending
        ) {
            markReadMutation.mutate(postPk);
        }
    }, [
        inquiryDetailQuery.data,
        inquiryDetailQuery.isSuccess,
        markReadMutation,
        postPk,
    ]);
    const initialView = isWriteOpen
        ? "write"
        : postPk
            ? "detail"
            : searchParams.get("screen") === "inquiry-complete" && isPreviewDataAllowed()
                ? "complete"
                : "list";

    const submitInquiry = async (payload: MobileInquirySubmitPayload) => {
        if (payload.files.length > 0) {
            throw new Error("현재 문의 첨부 파일은 지원하지 않습니다. 파일을 제외하고 등록해 주세요.");
        }
        await createInquiryMutation.mutateAsync({
            category: payload.category,
            subject: payload.title,
            message: payload.content,
        });
    };

    const errorMessage = !inquiryQuery.configured
        ? "MyStudio API 연결 설정을 확인해 주세요."
        : inquiryQuery.isError
            ? getMystudioErrorMessage(inquiryQuery.error)
            : "";
    const unavailableReason = inquiryQuery.data?.data?.availability === "unavailable"
        ? inquiryQuery.data.data.reasonCode || "문의 기능을 준비하고 있습니다."
        : "";

    return (
        <MobileInquiryPage
            initialView={initialView}
            selectedPost={selectedPost}
            posts={posts}
            isLoading={
                inquiryQuery.isLoading
                || inquiryQuery.isFetching
                || Boolean(postPk && inquiryDetailQuery.isLoading)
            }
            errorMessage={errorMessage}
            unavailableReason={unavailableReason}
            onRetry={() => inquiryQuery.refetch()}
            onBack={() => navigate(getQnaRoute("/tabs/home"))}
            onOpenWrite={() => navigate(getQnaRoute("/qnaList/write", "inquiry-write"))}
            onOpenList={() => navigate(getQnaRoute("/qnaList", "inquiry"))}
            onOpenDetail={(post) => {
                navigate(getQnaRoute(`/qnaList/${encodeURIComponent(String(post.post_pk))}`, "inquiry-detail"));
            }}
            onSubmit={submitInquiry}
            onReply={async (message) => {
                if (!postPk) return;
                await addMessageMutation.mutateAsync({ requestId: postPk, message });
                await inquiryDetailQuery.refetch();
            }}
        />
    );
}
